import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CustomModalComponent from '../../Elements/modal/CustomModal'
import { selectFormattedCurrency } from '../../../redux/selector/system'
import {
	cancelRoomChargeThunk,
	lookupRoomChargeGuestsThunk,
	submitRoomChargeThunk,
} from '../../../redux/thunk/roomCharge'
import { setRoomChargeErrorCode, setRoomChargeGuests } from '../../../redux/action/roomCharge'
import {
	selectRoomChargeErrorCode,
	selectRoomChargeGuests,
	selectRoomChargeGuestsAreLoading,
	selectRoomChargeIsProcessing,
	selectRoomChargeOrderId,
} from '../../../redux/selector/roomCharge'
import { selectIsPmsIntegrationConnected } from '../../../redux/selector/pmsIntegration'
import { useAppDispatch } from '../../../store/hooks'
import { Guest, RoomChargeResultStatus, RoomChargeStep, SearchMode } from '../types'
import { checkEligibility } from '../helper'
import RoomChargeModalHeader from './RoomChargeModalHeader'
import RoomChargeSearchStep from './RoomChargeSearchStep'
import RoomChargeSelectStep from './RoomChargeSelectStep'
import RoomChargeSignStep from './RoomChargeSignStep'
import RoomChargeResultStep from './RoomChargeResultStep'
import {
	RoomChargeModalBody,
	RoomChargeModalGlobalStyle,
	RoomChargeModalSteps,
	RoomChargeStepHint,
	RoomChargeStepHintIcon,
} from './index.styled'

const CustomModal = CustomModalComponent as unknown as React.ComponentType<Record<string, unknown> & { children?: React.ReactNode }>

const MAX_ROOM_LENGTH = 5
const MAX_NAME_LENGTH = 24
const EVENT_Z_INDEX = 36
const NO_SIGNATURE = 'NO_SIGNATURE'

type Props = {
	isOpen: boolean
	close: () => void
	tableLabel?: string
	total: number
	onCharged?: () => void
}

const RoomChargeModal = React.memo(({
	isOpen, close, tableLabel, total, onCharged,
}: Props) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()

	const orderId = useSelector(selectRoomChargeOrderId)
	const guests = useSelector(selectRoomChargeGuests)
	const guestsAreLoading = useSelector(selectRoomChargeGuestsAreLoading)
	const isProcessing = useSelector(selectRoomChargeIsProcessing)
	const errorCode = useSelector(selectRoomChargeErrorCode)
	const formattedTotal = useSelector(selectFormattedCurrency(total))
	const isPmsConnected = useSelector(selectIsPmsIntegrationConnected)

	const [step, setStep] = useState<RoomChargeStep>('search')
	const [searchMode, setSearchMode] = useState<SearchMode>('room')
	const [roomNumber, setRoomNumber] = useState('')
	const [guestName, setGuestName] = useState('')
	const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null)
	const [signature, setSignature] = useState<string | null>(null)
	const [notFound, setNotFound] = useState(false)
	const [resultStatus, setResultStatus] = useState<RoomChargeResultStatus>('processing')
	const [cancelBlocked, setCancelBlocked] = useState(false)
	const charged = useRef(false)

	const resetOnOpen = useCallback(() => {
		setStep('search')
		setSearchMode('room')
		setRoomNumber('')
		setGuestName('')
		setSelectedGuest(null)
		setSignature(null)
		setNotFound(false)
		setResultStatus('processing')
		setCancelBlocked(false)
		charged.current = false
		dispatch(setRoomChargeGuests([]))
		dispatch(setRoomChargeErrorCode(null))
	}, [dispatch])

	useEffect(() => {
		if (isOpen) resetOnOpen()
	}, [isOpen, resetOnOpen])

	const handleQueryChange = useCallback((value: string) => {
		setNotFound(false)
		dispatch(setRoomChargeErrorCode(null))
		if (searchMode === 'room') {
			setRoomNumber(value.replace(/\D/g, '').slice(0, MAX_ROOM_LENGTH))
		} else {
			setGuestName(value.slice(0, MAX_NAME_LENGTH))
		}
	}, [searchMode, dispatch])

	const handleSearchModeChange = useCallback((mode: SearchMode) => {
		setSearchMode(mode)
		setNotFound(false)
		dispatch(setRoomChargeErrorCode(null))
	}, [dispatch])

	const handleSearchSubmit = useCallback(async () => {
		if (!isPmsConnected) {
			setStep('sign')
			return
		}

		const params = searchMode === 'room' ? { roomNumber: roomNumber.trim() } : { lastName: guestName.trim() }
		const found = await dispatch(lookupRoomChargeGuestsThunk(params))

		if (!found.length) {
			setNotFound(true)
			return
		}
		setNotFound(false)
		setStep('select')
	}, [dispatch, isPmsConnected, searchMode, roomNumber, guestName])

	const handleSelectGuest = useCallback((guest: Guest) => {
		if (!checkEligibility(guest, total).ok) return
		setRoomNumber(guest.roomNumber)
		setSelectedGuest(guest)
		setStep('sign')
	}, [total])

	const handleCharge = useCallback(async (skipSignature: boolean) => {
		if (!orderId || (isPmsConnected && !selectedGuest)) return
		setResultStatus('processing')
		setStep('result')

		const outcome = await dispatch(submitRoomChargeThunk(orderId, {
			roomNumber: selectedGuest?.roomNumber ?? roomNumber.trim(),
			guestId: selectedGuest?.guestId,
			signature: {
				image: skipSignature ? NO_SIGNATURE : signature ?? NO_SIGNATURE,
				signerName: selectedGuest?.fullName,
			},
		}))

		if (outcome.status === 'POSTED') {
			charged.current = true
			onCharged?.()
			close()
			return
		}
		setResultStatus('error')
	}, [dispatch, orderId, isPmsConnected, selectedGuest, roomNumber, signature, onCharged, close])

	const handleRetry = useCallback(() => {
		dispatch(setRoomChargeErrorCode(null))
		setStep(guests.length ? 'select' : 'search')
	}, [dispatch, guests.length])

	const handleExit = useCallback(async () => {
		if (charged.current || !orderId) {
			close()
			return
		}
		const { unavailable } = await dispatch(cancelRoomChargeThunk(orderId))
		if (unavailable) {
			setCancelBlocked(true)
			return
		}
		close()
	}, [dispatch, orderId, close])

	const handleBack = useCallback(() => {
		if (step === 'search') {
			handleExit()
		} else {
			setStep(step === 'sign' && isPmsConnected ? 'select' : 'search')
		}
	}, [step, isPmsConnected, handleExit])

	const errorMessage = useMemo(() => {
		if (notFound) {
			return searchMode === 'room'
				? t('RoomCharge.errors.noGuestInRoom', { room: roomNumber })
				: t('RoomCharge.errors.noGuestWithName', { name: guestName.trim() })
		}
		if (!errorCode) return null
		return t(`RoomCharge.errors.${errorCode}`, { defaultValue: t('RoomCharge.errors.generic') })
	}, [notFound, searchMode, roomNumber, guestName, errorCode, t])

	const roomLimit = useMemo(() => {
		if (searchMode !== 'room') return null
		return guests[0]?.eligibility.creditRemaining ?? null
	}, [searchMode, guests])

	const showBackArrow = step === 'search' || step === 'select' || step === 'sign'

	return (
		<CustomModal isOpen={isOpen}
								 close={handleExit}
								 heading={true}
								 size="small"
								 id="room-charge-modal"
								 zIndex={EVENT_Z_INDEX}
								 enableOutsideClick={false}
								 disableBackButton={true}>
			<RoomChargeModalGlobalStyle />
			<RoomChargeModalBody>
				<RoomChargeModalHeader showBack={showBackArrow}
															 onBack={handleBack}
															 tableLabel={tableLabel}
															 total={total} />

				{cancelBlocked &&
					<RoomChargeStepHint>
						<RoomChargeStepHintIcon icon="alert-triangle" />
						{t('RoomCharge.errors.cancelUnavailable')}
					</RoomChargeStepHint>}

				<RoomChargeModalSteps>
					{step === 'search' &&
						<RoomChargeSearchStep searchMode={searchMode}
																	canSearchGuest={isPmsConnected}
																	roomNumber={roomNumber}
																	guestName={guestName}
																	maxNameLength={MAX_NAME_LENGTH}
																	errorMessage={errorMessage}
																	isLoading={guestsAreLoading}
																	onSearchModeChange={handleSearchModeChange}
																	onQueryChange={handleQueryChange}
																	onSubmit={handleSearchSubmit} />}

					{step === 'select' && guests.length > 0 &&
						<RoomChargeSelectStep guests={guests}
																	searchMode={searchMode}
																	roomNumber={roomNumber}
																	guestName={guestName}
																	total={total}
																	roomLimit={roomLimit}
																	onSelect={handleSelectGuest} />}

					{step === 'sign' && (selectedGuest || !isPmsConnected) &&
						<RoomChargeSignStep guest={selectedGuest}
																roomNumber={roomNumber}
																formattedTotal={formattedTotal}
																signature={signature}
																isPosting={isProcessing}
																canSkipSignature={!isPmsConnected}
																errorMessage={errorMessage}
																onSignatureChange={setSignature}
																onSubmit={handleCharge} />}

					{step === 'result' &&
						<RoomChargeResultStep status={resultStatus}
																	guestName={selectedGuest?.fullName}
																	roomNumber={roomNumber}
																	errorCode={errorCode}
																	errorMessage={errorMessage}
																	onRetry={handleRetry} />}
				</RoomChargeModalSteps>
			</RoomChargeModalBody>
		</CustomModal>
	)
})

RoomChargeModal.displayName = 'RoomChargeModal'
export default RoomChargeModal
